import { useEffect, useRef } from 'react';
import type { Claim } from '../types';
import { deadlineLead, useAppealWindows, withAppealWindow } from '../appealWindows';
import { daysLeft, expectedFor, formatDateFull, money } from '../utils';
import { expectedTotalOf, shortOf } from '../store';
import { SourceRow, claimSrcContext } from './popovers';
import { AppealRecommendation, HowElvaSends, LetterCard, Packet } from './DeniedDetail';
import { ActionArea } from './actions';
import type { ActionHandlers } from './actions';

interface Props {
  claim: Claim;
  handlers: ActionHandlers;
  onPreview: () => void;
  onBack?: () => void;
}

export function UnderpaidDetail(props: Props) {
  const { claim } = props;
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [claim.id]);

  const expected = expectedTotalOf(claim);
  const short = shortOf(claim);
  const proc = claim.procedures[0];
  return (
    <main className="apl-detail">
      {props.onBack ? (
        <button type="button" className="apl-back" onClick={props.onBack}>
          ← All claims
        </button>
      ) : null}
      <div className="apl-detail-inner">
        {/* header */}
        <section className="apl-claimhead" aria-labelledby="apl-name">
          <div>
            <h2 id="apl-name" className="apl-patient" tabIndex={-1} ref={headingRef}>
              {claim.patient}
            </h2>
            <p className="apl-chsub">
              {claim.payer.name} · member {claim.memberId} · service {claim.serviceDate} · {claim.provider}
            </p>
            <p className="apl-chmeta">
              {proc ? `${proc.code}${proc.tooth ? ` ${proc.tooth}` : ''}` : ''} · claim {claim.id} · paid {claim.paidDate}
            </p>
          </div>
          <div className="apl-right">
            <div className="apl-meta">Short</div>
            <div className="apl-bigfig num">{money(short)}</div>
            <div className="apl-meta">
              Expected {money(expected)} · {claim.payer.short} paid {money(claim.paid ?? 0)}
            </div>
          </div>
        </section>

        {/* table */}
        <section className="apl-card" aria-labelledby="apl-paidtable">
          <h3 className="apl-h3" id="apl-paidtable" style={{ marginBottom: 12 }}>
            What {claim.payer.short} paid, and what it should have
          </h3>
          <div className="apl-tbl-wrap">
            <table className="apl-tbl">
              <thead>
                <tr>
                  <th>Code</th>
                  <th className="ta-right">Billed</th>
                  <th className="ta-right">Your {claim.payer.short} fee</th>
                  <th className="ta-right">Fee {claim.payer.short} used</th>
                  <th className="ta-right">Plan pays</th>
                  <th className="ta-right">Expected</th>
                  <th className="ta-right">Paid</th>
                </tr>
              </thead>
              <tbody>
                {(claim.table ?? []).map((row) => {
                  const exp = expectedFor(row, claim.planRemaining ?? Infinity).expected;
                  return (
                    <tr key={row.code}>
                      <td style={{ fontWeight: 700 }}>{row.code}</td>
                      <td className="ta-right num">{money(row.billed)}</td>
                      <td className="ta-right num">{money(row.yourFee)}</td>
                      <td className="ta-right num bold">
                        {money(row.feeUsed)} {row.feeUsedNote ?? ''}
                      </td>
                      <td className="ta-right num">{row.planPaysPct}%</td>
                      <td className="ta-right num">{money(exp)}</td>
                      <td className="ta-right num bold">{money(row.paid)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="apl-tbl-foot">{claim.tableFooter}</div>
        </section>

        {/* why */}
        <section className="apl-card" aria-labelledby="apl-why">
          <span className="apl-eyebrow">{claim.why.eyebrow}</span>
          <h3 className="apl-h3" id="apl-why" style={{ margin: '6px 0 0' }}>
            {claim.why.headline}
          </h3>
          {claim.why.body ? <p style={{ margin: '8px 0 0', color: 'var(--fg2)' }}>{claim.why.body}</p> : null}
          <SourceRow sources={claim.why.sources} context={claimSrcContext(claim)} />
        </section>

        {/* recommendation + action */}
        {claim.answer === 'reprocess' && !claim.removed && !claim.reprocessRef && !claim.resolution ? (
          <ReprocessRecommendation claim={claim} />
        ) : null}
        {claim.answer !== 'reprocess' ? (
          <>
            <AppealRecommendation claim={claim} />
            {claim.packet ? <Packet claim={claim} onPreview={props.onPreview} /> : null}
            {claim.letter ? <LetterCard claim={claim} /> : null}
            <HowElvaSends claim={claim} />
          </>
        ) : null}
        <ActionArea claim={claim} h={props.handlers} />

        <p className="apl-history">History: {claim.history.join(' · ')}</p>
      </div>
    </main>
  );
}

function ReprocessRecommendation({ claim }: { claim: Claim }) {
  useAppealWindows();
  const r = claim.reprocess;
  if (!r) return null;
  const deadline = withAppealWindow(r.deadline, claim.payer, 'payment');
  return (
    <section className="apl-rec" aria-labelledby="apl-rec-title">
      <div className="apl-rec-head">
        <h3 className="apl-rec-title" id="apl-rec-title">{claim.recommendationHeader}</h3>
        <span className="apl-rec-deadline">
          {deadlineLead(deadline)} {formatDateFull(deadline.date)} · {daysLeft(deadline.date)} days left · {deadline.basis}
        </span>
      </div>
      <div className="apl-rec-body">
        <p style={{ margin: '0 0 12px' }}>{r.body}</p>
        <div className="apl-inner">
          <span className="apl-eyebrow">What ELVA will do</span>
          <p style={{ margin: '6px 0 0' }}>{r.elvaWillDo}</p>
        </div>
      </div>
    </section>
  );
}
